import {useQuery} from '@aboutyou/data-fetcher';
import {Log} from '@aboutyou/mlm';
import {
  doAsync,
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from '@aboutyou/utils';
import {SESSION_CACHE_KEY} from '../../CacheKeys';
import {createDataStore} from '../createDataStore';
import {TADARIDA_SESSION_PERSIST_KEY} from '../userStore/constants';
import {SetSessionIdError} from './errors';

export type SessionState = {sessionId: string} | null;

const useExpensiveSessionId = () => {
  const query = useQuery(
    SESSION_CACHE_KEY,
    async (): Promise<SessionState> => {
      const persisted = globalThis.localStorage
        ? localStorageGetItem(TADARIDA_SESSION_PERSIST_KEY)
        : null;
      const session = persisted ? JSON.parse(persisted) : null;
      if (session) {
        return session;
      }

      let sessionId = '';

      if (process.env.NODE_ENV === 'test') {
        sessionId = 'test-session-id';
      } else {
        let _sessionId;
        // the browser support for crypto.randomUUID is not great, so we use uuid as fallback
        if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
          _sessionId = crypto.randomUUID();
        } else {
          _sessionId = (await import('uuid')).v4();
        }
        sessionId = _sessionId.replace(/-/g, ''); // remove dashes from the generated id to make it compatible with the mAPI, this also works with desktop(iRIS)
      }

      if (sessionId) {
        return {sessionId};
      }
      throw new SetSessionIdError('Failed setting SessionId');
    },
    {
      staleTime: Infinity,
      onError: onGenerateSessionIdError,
      onSuccess: sessionState => {
        // the session id is saved to `__SESSION_ID__` only to make some e2e tests easier, where we prefetch or prefill the basket/wishlist
        // for a given session id and then compare to what is rendered on the page.
        // Do not use window.__SESSION_ID__ for anything features related.
        // hence its not typed.
        // @ts-expect-error intentionally not typed
        window.__SESSION_ID__ = sessionState?.sessionId;
      },
    },
  );

  // the session update is normally when the user logs in or logs out, or when session validation on page load happens.
  // In cases of these intentional updates, we want to persist the session to local storage. so that the same session can be retrieved on page reload.
  const updateCache = (sessionState: SessionState) => {
    query.updateCache(sessionState);
    persistSessionState(sessionState);
  };

  return {...query, updateCache};
};

const {Provider, useData: useSessionId} = createDataStore<SessionState>({
  defaultValue: null,
  useQueryHook: useExpensiveSessionId,
});

export const usePersistSessionStateCallback = () => {
  const {data} = useSessionId();
  return () => {
    if (data) {
      persistSessionState(data);
    }
  };
};

const persistSessionState = (sessionState: SessionState) => {
  doAsync(() => {
    // we set the sessionState to null on logout
    // so we need to remove the persisted session from local storage
    if (sessionState === null) {
      localStorageRemoveItem(TADARIDA_SESSION_PERSIST_KEY);
      return;
    }

    localStorageSetItem(
      TADARIDA_SESSION_PERSIST_KEY,
      JSON.stringify(sessionState),
    );
  });
};

const onGenerateSessionIdError = (error: unknown) => {
  Log.error('@aboutyou/feature', 'Failed getting a SessionId', {
    error: error as Error,
  });
};

export {Provider as SessionProvider, useSessionId};
